import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'

import { AgGrid } from '../../../../components'
import CustomCellRender from './cell/cell.component'
import { getDefaultColumnDef, preventDefaultRowClick } from '../../../../utils'

/**
 * Custom Contact Grid
 */
class CustomContactGrid extends PureComponent {
  /**
   * Constructor
   * @param props
   */
  constructor (props) {
    super(props)
    this.state = {
      columns: [
        { ...getDefaultColumnDef(0), field: '_id', headerName: 'Record ID', minWidth: 220, maxWidth: 220 },
        { ...getDefaultColumnDef(1, 'text'), field: 'full_name', headerName: 'Name', minWidth: 380 },
        { ...getDefaultColumnDef(2), field: 'score', headerName: 'Score', minWidth: 150, maxWidth: 150, sortable: true }
      ]
    }
  }

  /**
   * Handle AgGrid onGridReady event
   * @param grid
   * @see: {@link: https://github.com/ag-grid/ag-grid/issues/997}
   */
  handleGridReady = (grid) => {
    setTimeout(() => this.agGrid = grid, 0)
  }

  /**
   * Handle AgGrid gridSizeChanged event
   * @param type - event type
   */
  handleGridResize = ({ type }) => {
    if (type === 'gridSizeChanged') {
      setTimeout(() => this.agGrid && this.agGrid.api.sizeColumnsToFit(), 0)
    }
  }

  /**
   * Returns grid columns
   */
  getColumnsDefinition = () => {
    return this.state.columns.map(({ typeOfValue, ...column }, idx) => ({
      ...getDefaultColumnDef(idx, typeOfValue),
      ...column
    }))
  }

  /**
   * Renders CustomCell Component
   */
  renderCustomCell = (props) => {
    return (<CustomCellRender {...props} />)
  }

  /**
   * Handle Sorting Data by Column
   * @param grid
   */
  handleSortChange = (grid) => {
    const { handleFilterChange } = this.props;
    const api = grid && grid.api;
    const sortModel = api && api.getSortModel();

    if (!sortModel && !sortModel.length) {
      return
    }

    handleFilterChange && handleFilterChange({
      sort: {
        property: sortModel[0].colId,
        direction: sortModel[0].sort
      }
    })
  }

  /**
   * Paginate contact data and order by sorted column, if specified
   * @return {*[]}
   */
  paginateData () {
    const { data, filters } = this.props
    const { page, limit, sort } = filters
    const contacts = sort ? this.sortByScore() : data

    return contacts.slice((page - 1) * limit, (page - 1) * limit + limit)
  }

  /**
   * Sort data by score and direction
   * Returns scores first in ascending or descending order, followed by all "In Progress" results
   * @return {*[]}
   */
  sortByScore () {
    const { data, filters } = this.props
    const { sort } = filters
    const { direction } = sort

    const hasScore = data
      .filter((i) => i && typeof i.score === 'number')
      .sort((a, b) => direction === 'asc' ? a.score - b.score : b.score - a.score)
    const hasNoScore = data.filter((i) => i && ((typeof i.score !== 'number') || !i.score))

    return [].concat(hasScore, hasNoScore)
  }

  /**
   * render
   * @return {*}
   */
  render () {
    const { isGridReady, filters, total, handleFilterChange, changeContactMatch } = this.props
    const { page, limit } = filters
    return (
      <>
        {isGridReady && (
          <AgGrid
            className='custom-contact-grid'
            domLayout='autoHeight'
            sizeToFit

            // suppress configs
            suppressMovableColumns
            suppressContextMenu

            // columns and data
            defaultColDef={{
              suppressMenu: true,
              sortable: false,
              cellRenderer: 'CustomCellRender'
            }}
            columnDefs={this.getColumnsDefinition()}
            rowData={this.paginateData()}

            // pagination
            pagination
            paginationProps={{
              pageSizeId: 'custom-contact-grid',
              forcePage: page,
              initialPageSize: limit,
              showPageSizeSelection: true,
              total,
              onPageChange: ({ selected }) => handleFilterChange({ page: selected }),
              onPageSizeChange: ({ selected }) => handleFilterChange({ page: 1, limit: selected })
            }}

            // custom components
            frameworkComponents={{
              CustomCellRender: this.renderCustomCell
            }}

            // event listeners
            onGridReady={this.handleGridReady}
            onGridSizeChanged={this.handleGridResize}
            onRowClicked={preventDefaultRowClick(changeContactMatch)}
            onSortChanged={this.handleSortChange}
          />
        )}
      </>
    )
  }
}

CustomContactGrid.propTypes = {
  isGridReady: PropTypes.bool.isRequired,
  data: PropTypes.array.isRequired,
  total: PropTypes.number.isRequired,
  filters: PropTypes.shape({
    page: PropTypes.number.isRequired,
    limit: PropTypes.number.isRequired,
    sort: PropTypes.shape({
      property: PropTypes.string,
      direction: PropTypes.string
    })
  }),
  handleFilterChange: PropTypes.func.isRequired
}

CustomContactGrid.defaultProps = {
  data: [],
  total: 0
}

export default CustomContactGrid